import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPhotoAnnotation,
  savePhotoAnnotation,
} from '../../../../lib/inspections';
import { apiErrorMessage } from '../../../../lib/api';
import { useToastStore } from '../../../../stores/toast';
import type { AnnotationStroke, AnnotationData, AnnotationConfig, ActiveTool } from './types';
import { COLOR_PALETTE } from './types';
import { findStrokeAtPoint, drawStroke } from './drawing-utils';

interface UsePhotoAnnotationOptions {
  /** annotation config 전달 시 활성화 */
  config: AnnotationConfig | undefined;
  /** 현재 표시 중인 사진 ID */
  currentPhotoId: string | undefined;
  isOpen: boolean;
  /** 캔버스 ref */
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  /** 캔버스 크기 */
  canvasSize: { width: number; height: number };
  /** 줌 레벨 (좌표 변환용) */
  zoom: number;
}

export function usePhotoAnnotation({
  config,
  currentPhotoId,
  isOpen,
  canvasRef,
  canvasSize,
  zoom,
}: UsePhotoAnnotationOptions) {
  const enabled = !!config;
  const readonly = config?.readonly ?? false;
  const skipAnnotation = config?.skipAnnotation ?? false;
  const companyId = config?.companyId ?? '';
  const inspectionId = config?.inspectionId ?? '';
  const initialShowAnnotation = config?.initialShowAnnotation ?? true;
  const customApi = config?.customApi;

  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.showToast);

  // 그리기 상태
  const [showAnnotation, setShowAnnotation] = useState(initialShowAnnotation);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentColor, setCurrentColor] = useState(COLOR_PALETTE[0].value);
  const [currentWidth, setCurrentWidth] = useState(4);
  const [activeTool, setActiveTool] = useState<ActiveTool>('draw');
  const [customColor, setCustomColor] = useState('#ff0000');
  const [strokes, setStrokes] = useState<AnnotationStroke[]>([]);
  const [currentStroke, setCurrentStroke] = useState<AnnotationStroke | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [panelLayout, setPanelLayout] = useState<'side' | 'bottom'>('side');

  const isEraserMode = activeTool === 'eraser';
  const isPanTool = activeTool === 'pan';

  // 멀티터치 추적
  const activePointers = useRef(new Map<number, { x: number; y: number }>());

  // 어노테이션 조회
  const queryKey = customApi
    ? [customApi.cacheKey, currentPhotoId]
    : ['photoAnnotation', inspectionId, currentPhotoId];
  const { data: annotationData, isLoading: isLoadingAnnotation } = useQuery({
    queryKey,
    queryFn: () => customApi
      ? customApi.get(currentPhotoId!)
      : getPhotoAnnotation(companyId, inspectionId, currentPhotoId!),
    enabled: enabled && isOpen && !!currentPhotoId && !skipAnnotation,
  });

  // 어노테이션 저장
  const saveMutation = useMutation({
    mutationFn: (data: AnnotationData) => customApi
      ? customApi.save(currentPhotoId!, data)
      : savePhotoAnnotation(companyId, inspectionId, currentPhotoId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customApi
          ? [customApi.cacheKey, currentPhotoId]
          : ['photoAnnotation', inspectionId, currentPhotoId],
      });
      setHasUnsavedChanges(false);
    },
    /**
     * ⚠️⚠️ **서버 거부를 «받아 줄 자리»다 — 없으면 뷰어가 잠긴다**(2026-10-13).
     *
     * 서버는 사진 쓰기 락(보고서가 생성된 점검)을 **400으로 거부**한다
     * (`inspection-photo.service.savePhotoAnnotation` — 그 JSDoc이 축 가름의 이유를 적는다).
     * 평시엔 web이 `readonly`에 `hasReport`를 실어 캔버스를 잠그므로 도달하지 않지만,
     * **그린 뒤 타인이 보고서를 생성하면 레이스로 도달한다.** 종전엔 이 사슬 어디에도 `onError`·
     * `try/catch`가 **0건**이어서 세 자리가 통째로 막다른 길이었다:
     *   ① `beforeClose`의 `await handleSave()` → `usePhotoNavigation.handleClose`가 `onClose()`에
     *      도달하지 못해 **뷰어가 안 닫히고** `body.overflow:hidden`이 남는다(새로고침 말고 길이 없다)
     *   ② `beforeNavigate` → 사진 이동이 막힌다
     *   ③ `PhotoViewer`의 저장 버튼(`onClick={anno.handleSave}`) → unhandled rejection + **피드백 0**
     *
     * ⚠️⚠️ **③은 ①②와 «다른 축»이고 그쪽이 훨씬 자주 발동한다.** ①②는 그 400 레이스가 전제지만,
     * ③은 **실패 이유를 가리지 않는다** — 네트워크 단절·401 만료·500 어느 것이든 사용자가
     * 「저장」을 눌렀는데 **아무 일도 안 일어난 것처럼 보인다**(버튼이 `hasUnsavedChanges`로 남아
     * 있으니 눌러도 눌러도 같다). 즉 ③은 레이스가 아니라 **상시 결함**이었다. 레이스 축만 생각하고
     * 이 `onError`를 「드문 경우를 위한 것」으로 읽지 말 것 — **이것이 저장 실패의 유일한 통지 경로**다.
     *
     * **왜 「막고 알리기」가 아니라 「알리고 닫히게」인가** — 그 400은 「보고서가 생겼다」라
     * **재시도해도 영영 성공하지 않는다.** 닫기를 막으면 빠져나갈 길이 없는 벽이 된다.
     * 그리기는 잃지만 그것은 서버가 그 점검을 잠근 결과이고, 사용자는 **이유를 안 채** 나간다.
     *
     * 문구는 서버가 준 것을 그대로 쓴다(`apiErrorMessage`) — 서버만이 「왜 잠겼나」를 안다.
     * 네트워크·알 수 없는 예외면 null이라 자기 일반 문구로 떨어진다(그 헬퍼 JSDoc이 정본).
     */
    onError: (error) => {
      showToast(apiErrorMessage(error) ?? '그리기를 저장하지 못했습니다.', 'error');
    },
  });

  // 어노테이션 데이터 로드
  useEffect(() => {
    if (!enabled) return;
    if (annotationData?.data) {
      setStrokes(annotationData.data.strokes || []);
    } else {
      setStrokes([]);
    }
    setHasUnsavedChanges(false);
  }, [enabled, annotationData, currentPhotoId]);

  // 모달 열릴 때 초기화
  useEffect(() => {
    if (isOpen && enabled) {
      setShowAnnotation(initialShowAnnotation);
    }
  }, [isOpen, enabled, initialShowAnnotation]);

  // 캔버스 다시 그리기
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !enabled) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!showAnnotation) return;

    for (const stroke of strokes) {
      drawStroke(ctx, stroke, canvasSize.width, canvasSize.height);
    }

    if (currentStroke) {
      drawStroke(ctx, currentStroke, canvasSize.width, canvasSize.height);
    }
  }, [enabled, strokes, currentStroke, showAnnotation, canvasSize]);

  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  // 좌표 변환 (캔버스 → 정규화)
  const toNormalizedPoint = useCallback((x: number, y: number) => ({
    x: Math.max(0, Math.min(1, x / canvasSize.width)),
    y: Math.max(0, Math.min(1, y / canvasSize.height)),
  }), [canvasSize]);

  // 포인터 이벤트에서 캔버스 좌표 추출 (줌 고려)
  const getCanvasCoords = useCallback((e: React.PointerEvent<HTMLCanvasElement> | PointerEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;
    return { x, y, pressure: (e as PointerEvent).pressure || 0.5 };
  }, [zoom]);

  /**
   * 저장 — **거부당해도 던지지 않는다**(알림은 위 `onError`가 한다). 반환값은 저장됐나.
   *
   * ⚠️ 호출부 셋 중 둘(`beforeClose`·`beforeNavigate`)은 **`usePhotoNavigation`이 `await`하고 그
   * 뒤에 닫기·이동을 하므로**, 여기서 던지면 그 뒤가 실행되지 않아 뷰어가 잠긴다. 그래서 이 함수의
   * 계약은 「성공하면 true, 실패하면 알리고 false」다 — **거부를 전파하지 않는 것이 기능이다.**
   */
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!hasUnsavedChanges || !enabled) return false;
    const data: AnnotationData = { version: 1, strokes };
    try {
      await saveMutation.mutateAsync(data);
      return true;
    } catch {
      // 사용자 알림은 `onError`가 이미 했다 — 여기서는 삼켜 호출부(닫기·이동)가 진행되게 한다.
      return false;
    }
  }, [hasUnsavedChanges, enabled, strokes, saveMutation]);

  // 이동 전 자동저장 콜백
  const beforeNavigate = useCallback(async () => {
    if (hasUnsavedChanges && enabled) {
      await handleSave();
    }
    setCurrentStroke(null);
  }, [hasUnsavedChanges, enabled, handleSave]);

  // 닫기 전 저장 확인
  const beforeClose = useCallback(async () => {
    if (hasUnsavedChanges && enabled) {
      const shouldSave = confirm('저장하지 않은 변경사항이 있습니다. 저장하시겠습니까?');
      if (shouldSave) {
        await handleSave();
      }
    }
  }, [hasUnsavedChanges, enabled, handleSave]);

  // 전체 지우기
  const handleClearAll = useCallback(() => {
    if (strokes.length === 0) return;
    if (!confirm('모든 그리기를 지우시겠습니까?')) return;
    setStrokes([]);
    setHasUnsavedChanges(true);
  }, [strokes.length]);

  // 그리기 시작 — 패닝 시작이 필요한 경우 true 반환
  const handleDrawPointerDown = useCallback((
    e: React.PointerEvent<HTMLCanvasElement>,
    startPanning: (e: React.PointerEvent) => void,
  ) => {
    if (!enabled) {
      startPanning(e);
      return;
    }

    // 멀티터치 추적
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // 두 손가락 이상 → 그리기 취소하고 패닝 전환
    if (activePointers.current.size >= 2) {
      if (isDrawing) {
        setIsDrawing(false);
        setCurrentStroke(null);
      }
      startPanning(e);
      return;
    }

    // Alt 클릭, 휠 클릭은 항상 패닝
    if (e.altKey || e.button === 1) {
      startPanning(e);
      return;
    }

    // readonly, 메모 숨김, 또는 이동 도구 선택 시 → 패닝
    if (readonly || !showAnnotation || isPanTool) {
      startPanning(e);
      return;
    }

    const coords = getCanvasCoords(e);
    if (!coords) return;

    if (isEraserMode) {
      const hitStroke = findStrokeAtPoint(coords.x, coords.y, strokes, canvasSize);
      if (hitStroke) {
        setStrokes(prev => prev.filter(s => s.id !== hitStroke.id));
        setHasUnsavedChanges(true);
      }
      return;
    }

    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDrawing(true);

    const normalizedPoint = toNormalizedPoint(coords.x, coords.y);
    const newStroke: AnnotationStroke = {
      id: crypto.randomUUID(),
      points: [{ ...normalizedPoint, pressure: coords.pressure }],
      color: currentColor,
      width: currentWidth,
      timestamp: Date.now(),
    };
    setCurrentStroke(newStroke);
  }, [enabled, readonly, showAnnotation, isEraserMode, isPanTool, isDrawing, getCanvasCoords, strokes, canvasSize, toNormalizedPoint, currentColor, currentWidth]);

  // 그리기 진행
  const handleDrawPointerMove = useCallback((
    e: React.PointerEvent<HTMLCanvasElement>,
    isPanningActive: boolean,
    handlePanMove: (e: React.PointerEvent) => void,
  ) => {
    if (!enabled) {
      handlePanMove(e);
      return;
    }

    // 멀티터치 포인터 위치 갱신
    if (activePointers.current.has(e.pointerId)) {
      activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    // 멀티터치 패닝은 상위에서 처리
    if (isPanningActive) {
      handlePanMove(e);
      return;
    }

    if (!isDrawing || !currentStroke || readonly) return;

    // getCoalescedEvents로 모든 포인트 수집
    const events = e.nativeEvent.getCoalescedEvents?.() || [e.nativeEvent];
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const newPoints = events.map(evt => {
      const x = (evt.clientX - rect.left) / zoom;
      const y = (evt.clientY - rect.top) / zoom;
      return {
        ...toNormalizedPoint(x, y),
        pressure: (evt as PointerEvent).pressure || 0.5,
      };
    });

    setCurrentStroke(prev => {
      if (!prev) return null;
      return { ...prev, points: [...prev.points, ...newPoints] };
    });
  }, [enabled, isDrawing, currentStroke, readonly, toNormalizedPoint, zoom]);

  // 그리기 종료
  const handleDrawPointerUp = useCallback((
    e: React.PointerEvent<HTMLCanvasElement>,
    isPanningActive: boolean,
    handlePanEnd: () => void,
  ) => {
    if (!enabled) {
      handlePanEnd();
      return;
    }

    // 멀티터치 추적 정리
    activePointers.current.delete(e.pointerId);

    if (isPanningActive) {
      if (activePointers.current.size < 2) {
        handlePanEnd();
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
      }
      return;
    }

    if (!isDrawing || !currentStroke) return;

    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
    setIsDrawing(false);

    if (currentStroke.points.length >= 2) {
      setStrokes(prev => [...prev, currentStroke]);
      setHasUnsavedChanges(true);
    }
    setCurrentStroke(null);
  }, [enabled, isDrawing, currentStroke]);

  return {
    // 상태
    enabled,
    readonly,
    showAnnotation,
    setShowAnnotation,
    activeTool,
    setActiveTool,
    isEraserMode,
    isPanTool,
    currentColor,
    setCurrentColor,
    currentWidth,
    setCurrentWidth,
    customColor,
    setCustomColor,
    strokes,
    hasUnsavedChanges,
    isLoadingAnnotation,
    isSaving: saveMutation.isPending,
    panelLayout,
    setPanelLayout,
    skipAnnotation,

    // 핸들러
    handleSave,
    handleClearAll,
    beforeNavigate,
    beforeClose,
    handleDrawPointerDown,
    handleDrawPointerMove,
    handleDrawPointerUp,
  };
}
